import React, { useEffect, useState, useCallback, useRef } from 'react';
import { trainers } from '../mock';
import BioSheet from './BioSheet';
import { ARACELY_URL, AMY_URL, ARACELY_BIO, AMY_BIO } from '../data/heroBios';

const LEFT_HOSTS = [
  {
    src: ARACELY_URL,
    alt: 'Aracely Garcia, Team Leader',
    badge: '★ TEAM LEADER',
    eyebrow: 'TEAM LEADER · GALT',
    first: 'ARACELY',
    last: 'GARCIA',
    caption: 'STRENGTH · COMMUNITY',
    bio: ARACELY_BIO,
  },
  {
    src: AMY_URL,
    alt: 'Amy Farris, Fitness Director',
    badge: '★ DIRECTOR',
    eyebrow: 'YOGA · 14 YRS',
    first: 'AMY',
    last: 'FARRIS',
    caption: 'DIRECTOR · WELLNESS',
    bio: AMY_BIO,
  },
];

// === MOBILE / iPad GALLERY ===
// Touch-only 11-photo slideshow that replaces the Aracely/Amy crossfade
// card on coarse-pointer / no-hover devices. Desktop is COMPLETELY
// untouched — keeps the existing Aracely↔Amy host crossfade + "TAP FOR
// BIO" affordance. Order matches the user's explicit request.
// Source images are 7 existing facility photos (already on disk) + 4
// new uploads (Merch / Welcome / Fitness Pro logo / Cardio Row, saved
// as gallery-*.webp at ≤720 px / ~20-50 KB each).
const MOBILE_GALLERY = [
  { src: '/img/xe5rqn3w_516953578_24685037217765020_5100632170340774470_n.webp', alt: 'Spin class on the floor',   caption: 'SPIN CLASS',    fit: 'cover' },
  { src: '/img/2dy6neoz_484325099_10057134077648576_363682762017151067_n.webp',  alt: 'Aspire strength zone',      caption: 'ASPIRE',        fit: 'cover' },
  { src: '/img/x9qmjfs8_471638303_18254282371278507_2919533489596847049_n.webp', alt: 'Member love community',     caption: 'MEMBER LOVE',   fit: 'cover' },
  { src: '/img/b8jhv0jv_469882866_18252266329278507_7261941386270321218_n.webp', alt: 'Juice bar fuel up',         caption: 'JUICE BAR',     fit: 'cover' },
  { src: '/img/gallery-juice-bar-menu.webp',                                    alt: 'Juice bar menu — Tropical Glow, Green Refresh, Ginger Zing, Golden Apple, Daily Glow, Bagels, Avocado Toast', caption: 'JUICE BAR MENU', fit: 'contain' },
  { src: '/img/magpa32v_484706394_10056032161092101_7585546575707359_n.webp',   alt: 'The floor',                 caption: 'THE FLOOR',     fit: 'cover' },
  { src: '/img/enet3en0_483494387_10038889756139675_1034494171014732905_n.webp', alt: 'Premium gear matrix',       caption: 'PREMIUM GEAR',  fit: 'cover' },
  { src: '/img/fzaqcmzn_485729502_23870148752587208_5875663392288346925_n.jpg',  alt: 'Kid care room',             caption: 'KID CARE',      fit: 'cover' },
  { src: '/img/gallery-merch.webp',                                             alt: 'FitnessPro merch lineup',   caption: 'MERCH',         fit: 'cover' },
  { src: '/img/gallery-welcome.webp',                                           alt: 'Welcome to your home town gym graphic', caption: 'WELCOME', fit: 'contain' },
  { src: '/img/gallery-fitnesspro.webp',                                        alt: 'FitnessPro Galt logo',      caption: 'FITNESS PRO',   fit: 'contain' },
  { src: '/img/gallery-cardio.webp',                                            alt: 'Cardio row machine line',   caption: 'CARDIO ROW',    fit: 'cover' },
];

function MobileGalleryCard() {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  const cardRef = useRef(null);

  // Pause autoplay when offscreen (IntersectionObserver) — saves CPU on
  // long pages where the strip lives well below the fold.
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => setVisible(entry.isIntersecting));
    }, { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Pause autoplay when tab is hidden — prevents wasted timers on
  // backgrounded Safari tabs that previously contributed to memory
  // pressure / image-eviction issues.
  useEffect(() => {
    const onVis = () => { if (document.hidden) setVisible(false); else setVisible(true); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Honor reduced-motion: disable autoplay entirely.
  const reducedMotion = typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Lightweight autoplay loop: 2 s interval per photo (mobile/iPad
  // spec). Paused if offscreen / tab hidden / reduced-motion. Inside
  // the tick a second `document.visibilityState` guard catches the case
  // where the user backgrounds the tab and the visibilitychange
  // listener fires AFTER the next interval — belt-and-suspenders.
  // Stops cleanly on unmount; the [visible, reducedMotion] deps ensure
  // only ONE interval is ever active (the previous one is cleared by
  // the cleanup return before a new one is registered).
  useEffect(() => {
    if (!visible || reducedMotion) return undefined;
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      setIdx((i) => (i + 1) % MOBILE_GALLERY.length);
    }, 2000);
    return () => clearInterval(id);
  }, [visible, reducedMotion]);

  const goPrev = useCallback(() => setIdx((i) => (i - 1 + MOBILE_GALLERY.length) % MOBILE_GALLERY.length), []);
  const goNext = useCallback(() => setIdx((i) => (i + 1) % MOBILE_GALLERY.length), []);

  // Only render current + next (and previous so prev tap is instant) —
  // never decode all 11 at once. Lazy decode preserves iPhone Safari
  // bitmap budget. Active image is eager + decoding=async; the two
  // adjacent are lazy preloaded so the next swap feels instant.
  const prevIdx = (idx - 1 + MOBILE_GALLERY.length) % MOBILE_GALLERY.length;
  const nextIdx = (idx + 1) % MOBILE_GALLERY.length;

  // Safari bfcache / idle-return recovery — on pageshow (especially the
  // persisted=true case), force the active <img> to re-fire onLoad by
  // toggling its src in place. Combined with the global
  // bfcacheImageRecovery listener this guarantees no blank gallery card
  // after the user returns to the tab 30-60 min later.
  useEffect(() => {
    const onShow = (e) => {
      if (!e.persisted && document.visibilityState !== 'visible') return;
      const el = cardRef.current;
      if (!el) return;
      const imgs = el.querySelectorAll('img');
      imgs.forEach((img) => {
        if (img.complete && img.naturalWidth === 0) {
          const src = img.src;
          img.src = '';
          img.src = src;
        }
      });
    };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, []);

  return (
    <div
      ref={cardRef}
      data-testid="mobile-gallery-card"
      className="strip-aracely relative w-full max-w-[560px] mx-auto h-full rounded-3xl overflow-hidden border-[3px] border-red-600/65 shadow-[0_24px_60px_rgba(239,43,43,0.4)] bg-zinc-900"
    >
      {/* Render up to 3 image elements simultaneously — active + prev + next.
          Only the active one is visible (opacity 1); the others are 0 but
          decoded in the background so transitions are instant. Lazy +
          decoding=async so iPhone Safari spreads decode work over time. */}
      {MOBILE_GALLERY.map((slide, i) => {
        const active = i === idx;
        const nearby = i === prevIdx || i === nextIdx;
        if (!active && !nearby) return null;
        return (
          <img
            key={slide.src}
            src={slide.src}
            alt={slide.alt}
            loading={active ? 'eager' : 'lazy'}
            decoding="async"
            className={`absolute inset-0 w-full h-full transition-opacity duration-500 ease-in-out ${slide.fit === 'contain' ? 'object-contain bg-black p-3' : 'object-cover'}`}
            style={{ opacity: active ? 1 : 0 }}
          />
        );
      })}

      {/* Bottom gradient for legibility */}
      <div className="absolute inset-x-0 bottom-0 h-[42%] bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none" />

      {/* Top pill — gallery indicator, no trainer wording */}
      <div className="absolute top-2 left-2 right-2 sm:top-3 sm:left-3 sm:right-3 z-10 flex items-center justify-between gap-1.5 sm:gap-2 pointer-events-none">
        <div className="px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-red-600 shadow-[0_4px_18px_rgba(239,43,43,0.5)]">
          <span className="block text-[8px] sm:text-[9px] tracking-[0.08em] sm:tracking-[0.3em] font-bold whitespace-nowrap text-white">★ GYM LIFE</span>
        </div>
        <div className="px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-black/75 backdrop-blur border border-red-500/55 text-[8px] sm:text-[9px] tracking-[0.08em] sm:tracking-[0.3em] text-white font-bold">
          {idx + 1} / {MOBILE_GALLERY.length}
        </div>
      </div>

      {/* Caption — fades with image */}
      <div className="absolute bottom-0 left-0 right-0 px-3 sm:px-4 pb-3 sm:pb-4 z-10 pointer-events-none">
        <div key={`cap-${idx}`} className="animate-host-fade">
          <div className="flex items-center gap-1.5 sm:gap-2 text-[8px] sm:text-[9px] tracking-[0.18em] sm:tracking-[0.4em] text-red-400 font-bold mb-1.5 whitespace-nowrap">
            <div className="h-[1.5px] w-4 sm:w-5 bg-red-500 flex-shrink-0" />
            <span className="truncate">GALT · CA</span>
          </div>
          <div className="font-display text-xl sm:text-2xl md:text-3xl text-white leading-none truncate" style={{ textShadow: '0 4px 20px rgba(0,0,0,0.95)' }}>
            {MOBILE_GALLERY[idx].caption}
          </div>
        </div>
      </div>

      {/* Manual nav controls — overlay buttons on left/right edges. Tappable
          even when autoplay is running; tapping doesn't permanently pause —
          it just advances the slide and lets autoplay continue. */}
      <button
        type="button"
        onClick={goPrev}
        aria-label="Previous photo"
        data-testid="mobile-gallery-prev"
        className="absolute left-1.5 top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full bg-black/55 backdrop-blur border border-white/15 flex items-center justify-center active:scale-90 transition-transform"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <button
        type="button"
        onClick={goNext}
        aria-label="Next photo"
        data-testid="mobile-gallery-next"
        className="absolute right-1.5 top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full bg-black/55 backdrop-blur border border-white/15 flex items-center justify-center active:scale-90 transition-transform"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    </div>
  );
}

// Trainer cards — pull image from mock.js (now using uploaded branded photos)
// We also stash the full trainer object on each card so a tap opens their full bio.
function makeTrainerCard(name, badge, caption) {
  const t = trainers.find((x) => x.name === name);
  if (!t) return null;
  return {
    src: t.image,
    first: t.first,
    last: t.last,
    badge,
    caption,
    bio: {
      src: t.image,
      first: t.first,
      last: t.last,
      badge,
      role: t.role,
      summary: t.summary,
      bio: t.bio,
      phone: t.phone,
      email: t.email,
      instagram: t.instagram,
      location: 'GALT · CA',
    },
  };
}

const TRAINER_CARDS = [
  // Order: Amy (Director) → Bailey → Julio → Josie  (Valerie excluded sitewide)
  makeTrainerCard('Amy Farris',     'DIRECTOR', 'YOGA THERAPY · 14 YRS'),
  makeTrainerCard('Bailey Davis',   '★ COACH',  'WOMEN\u2019S STRENGTH · NASM'),
  makeTrainerCard('Julio Camberos', '★ COACH',  'STRENGTH · NUTRITION'),
  makeTrainerCard('Josie Davila',   '★ COACH',  'LIFESTYLE · BE FIT'),
].filter(Boolean);

// Real fitnessprogalt.com facility imagery — user-uploaded photos
const FACILITY_CARDS = [
  { src: '/img/co4c7pcl_unnamed.webp',                                          name: 'THE GYM',         tag: 'GALT · CA' },
  { src: '/img/xe5rqn3w_516953578_24685037217765020_5100632170340774470_n.webp', name: 'SPIN CLASS',      tag: 'FULL SEND' },
  { src: '/img/z0gjf64y_515515034_24685037311098344_5745537577007002013_n.jpg', name: 'BOOT CAMP',       tag: 'CORE WORK' },
  { src: '/img/73lb0lei_517115777_24689383973997011_4698949226023391178_n.webp', name: 'COMMUNITY',       tag: 'PACKED HOUSE' },
  { src: '/img/q9ccz8np_38678777_2193846197310776_4761543294310154240_n.webp',   name: 'SPIN STUDIO',     tag: '20+ BIKES' },
  { src: '/img/60bj5cs7_53827795_2543472662348126_5741364620317163520_n.webp',   name: 'MERCH',           tag: 'TEAM COLORS' },
  { src: '/img/3snb5sm8_81965220_3128893763806010_9216161959290863616_n.webp',   name: 'PRO SHOP',        tag: 'SUPPLEMENTS' },
  { src: '/img/uyilyy7j_476805647_9831763286852324_6190036691368351188_n.webp',  name: 'BOOT CAMP CREW',  tag: 'MORNING' },
  { src: '/img/99mxqcrt_475873641_9831766403518679_485768665049590823_n.webp',   name: 'MEMBER WINS',     tag: 'CELEBRATIONS' },
  { src: '/img/enet3en0_483494387_10038889756139675_1034494171014732905_n.webp', name: 'PREMIUM GEAR',    tag: 'MATRIX' },
  { src: '/img/magpa32v_484706394_10056032161092101_7585546575707359_n.webp',   name: 'THE FLOOR',       tag: 'UNSTOPPABLE' },
  { src: '/img/2dy6neoz_484325099_10057134077648576_363682762017151067_n.webp',  name: 'ASPIRE',          tag: 'STRENGTH ZONE' },
  { src: '/img/egco551e_485143493_10066754146686569_4384110597401864684_n.webp', name: 'SPIN BUDDIES',    tag: 'RIDE TOGETHER' },
  { src: '/img/fzaqcmzn_485729502_23870148752587208_5875663392288346925_n.jpg', name: 'KID CARE',        tag: 'EASTER FUN' },
  { src: '/img/kegezfhc_473029158_1123341896370709_6239375185746544538_n.jpg',  name: 'POWER CAGE',      tag: 'FUNCTIONAL' },
  { src: '/img/l0hjumaq_474779520_1133483392023226_5685135814415562765_n.webp',  name: 'LEGACY',          tag: '34 YEARS STRONG' },
  { src: '/img/7n7ibp00_482086832_1169147431790155_7484360314294768000_n.webp',  name: 'PINK OUT',        tag: 'CANCER AWARENESS' },
  { src: '/img/lupvma22_483101256_1174709554567276_4897601870202682214_n.webp',  name: 'PARTNER POWER',   tag: 'STRENGTH' },
  { src: '/img/f0htz03x_490267991_1200195682018663_7827012011101568272_n.webp',  name: 'MEMBER GAINS',    tag: 'CONSISTENCY' },
  { src: '/img/m0ugut83_grok-image-5a4d5fd6-1ff4-48b4-9219-1672b90a84a4.webp',   name: 'FORM FOCUS',      tag: 'KETTLEBELL' },
  { src: '/img/b8jhv0jv_469882866_18252266329278507_7261941386270321218_n.webp', name: 'JUICE BAR',       tag: 'FUEL UP' },
  { src: '/img/eeql9qib_492543653_1213259980712233_1881135757644614727_n.webp',  name: 'BRIDE TRIBE',     tag: 'WORKOUT NIGHT' },
  { src: '/img/72q3kum9_469583326_18252364462278507_2628258260122976675_n.jpg', name: 'CHEERS',          tag: 'JUICE BAR CREW' },
  { src: '/img/g9tz5umc_493867543_1215146417190256_2922107197334873181_n.webp',  name: 'HALLOWEEN',       tag: 'COSTUME DAY' },
  { src: '/img/x9qmjfs8_471638303_18254282371278507_2919533489596847049_n.webp', name: 'MEMBER LOVE',     tag: 'TIE-DYE DAY' },
  { src: '/img/ebbviyp0_495150474_1224566739581557_5073663827186956126_n.jpg',  name: 'FAREWELL',        tag: 'TEAM LOVE' },
  { src: '/img/wrl9uqw9_495004364_1224565396248358_6229174299260717813_n.jpg',  name: 'FLOOR CLASS',     tag: 'CORE WORK' },
  { src: '/img/bd7b6klc_519541850_1285475193490711_4899458451964641455_n.webp',  name: 'MEMBER MONDAY',   tag: '15+ YEARS' },
  { src: '/img/ssl7dul4_535094113_1310080871030143_8179902399828609783_n.webp',  name: 'LOYALTY',         tag: '28+ YEARS' },
  { src: '/img/2blvw1v4_574876371_1380694050635491_9222535174892457956_n.webp',  name: '1-ON-1 TRAINING', tag: 'CUSTOM PLAN' },
  { src: '/img/gallery-juice-bar-menu.webp',                                    name: 'JUICE BAR MENU',  tag: 'GALT · CA', fit: 'contain' },
];

// Final track — facility photos are shuffled on every page load for fresh
// visual rhythm; the 4 trainer cards are inserted at evenly-spaced positions
// so every face remains visible regardless of shuffle.
function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildTrack() {
  const facilities = shuffle(FACILITY_CARDS).map((f) => ({ kind: 'facility', ...f }));
  const trainers = TRAINER_CARDS.map((t) => ({ kind: 'trainer', ...t }));

  // Drop the 4 trainers at evenly-spaced positions so they're never clumped.
  const totalSlots = facilities.length + trainers.length;
  const stride = Math.floor(totalSlots / trainers.length);
  const items = [];
  let fi = 0;
  for (let i = 0; i < totalSlots; i++) {
    const trainerIdx = Math.floor(i / stride);
    if (i % stride === 0 && trainerIdx < trainers.length) {
      items.push(trainers[trainerIdx]);
    } else if (fi < facilities.length) {
      items.push(facilities[fi++]);
    }
  }
  // Append any leftover facility photos
  while (fi < facilities.length) items.push(facilities[fi++]);
  return items;
}

const TRACK = buildTrack();

// ---- iPhone Safari / older-device memory safety ----
// On coarse-pointer (mobile) devices the desktop right-side scrolling
// track is REMOVED entirely from the render tree (see HeroDemoStrip
// branch below) and replaced by the 11-photo MobileGalleryCard at full
// width. Mounting 66+ decoded image cards above the fold was the
// dominant cause of "A problem repeatedly occurred" Safari memory
// crashes; the mobile branch now mounts <= 3 decoded images at a time.
const IS_COARSE = typeof window !== 'undefined' && window.matchMedia
  && (window.matchMedia('(pointer: coarse)').matches
      || window.matchMedia('(hover: none)').matches);

function TrackCard({ item, index, isTrainer, onOpenBio }) {
  const aspectClass = isTrainer ? 'aspect-[3/4]' : 'aspect-[4/5]';
  const accentColor = item.kind === 'class' ? 'border-red-600/50' : item.kind === 'trainer' ? 'border-red-600/55' : 'border-red-600/30';

  const tappable = isTrainer && item.bio;
  const handleClick = tappable ? () => onOpenBio(item.bio) : undefined;
  const handleKey = tappable
    ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpenBio(item.bio);
        }
      }
    : undefined;

  return (
    <div
      className={`relative ${aspectClass} h-full flex-shrink-0 rounded-2xl overflow-hidden border-2 ${accentColor} shadow-[0_18px_40px_rgba(0,0,0,0.6)] group/card bg-zinc-900 ${tappable ? 'cursor-pointer transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98]' : ''}`}
      data-cursor={tappable ? 'hover' : undefined}
      data-testid={tappable ? `bio-trigger-${(item.first || '').toLowerCase()}` : undefined}
      role={tappable ? 'button' : undefined}
      tabIndex={tappable ? 0 : undefined}
      aria-label={tappable ? `Read bio for ${item.first} ${item.last}` : undefined}
      onClick={handleClick}
      onKeyDown={handleKey}
      style={{ transform: 'translateZ(0)', contain: 'layout paint' }}
    >
      <img
        src={item.src}
        alt={item.first ? `${item.first} ${item.last}` : item.name}
        loading="lazy"
        decoding="async"
        className={`absolute inset-0 w-full h-full ${item.fit === 'contain' ? 'object-contain bg-black p-2' : 'object-cover'}`}
        style={isTrainer ? { objectPosition: 'center 18%' } : undefined}
      />

      {isTrainer ? (
        // === TRAINER CARD — uses EXACT same overlay treatment as Aracely's host card ===
        <>
          {/* Edge gradients (same as Aracely) */}
          <div className="absolute inset-x-0 bottom-0 h-[42%] bg-gradient-to-t from-black via-black/40 to-transparent" />
          <div className="absolute inset-x-0 top-0 h-[14%] bg-gradient-to-b from-black/60 to-transparent" />

          {/* Top row — badge + index pip share one flex container so they
              never overlap, regardless of viewport width. */}
          <div className="absolute top-2 left-2 right-2 sm:top-3 sm:left-3 sm:right-3 z-10 flex items-center justify-between gap-1.5 sm:gap-2">
            <div className="flex-shrink min-w-0 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-red-600 shadow-[0_4px_18px_rgba(239,43,43,0.5)]">
              <span className="block text-[8px] sm:text-[9px] tracking-[0.08em] sm:tracking-[0.3em] font-bold whitespace-nowrap">{item.badge}</span>
            </div>
            <div className="flex-shrink-0 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-black/75 backdrop-blur border border-red-500/55">
              <span className="text-[8px] sm:text-[9px] tracking-[0.08em] sm:tracking-[0.3em] text-white font-bold">{String(index + 1).padStart(2, '0')}</span>
            </div>
          </div>

          {/* Bottom name plate — IDENTICAL to Aracely: white first name + red last name */}
          <div className="absolute bottom-0 left-0 right-0 px-3 sm:px-4 pb-3 sm:pb-4 z-10">
            <div className="flex items-center gap-1.5 sm:gap-2 text-[8px] sm:text-[9px] tracking-[0.18em] sm:tracking-[0.4em] text-red-400 font-bold mb-1.5 whitespace-nowrap">
              <div className="h-[1.5px] w-4 sm:w-5 bg-red-500 flex-shrink-0" />
              <span className="truncate">{item.badge === 'DIRECTOR' ? 'FITNESS DIRECTOR' : 'CERTIFIED COACH'}</span>
            </div>
            <div className="font-display text-xl sm:text-2xl md:text-3xl text-white leading-none truncate" style={{ textShadow: '0 4px 20px rgba(0,0,0,0.95)' }}>
              {item.first} <span className="text-red-500">{item.last}</span>
            </div>
            <div className="mt-1.5 sm:mt-2 flex items-center gap-2">
              <div className="text-[8px] sm:text-[9px] tracking-[0.12em] sm:tracking-[0.3em] text-zinc-300 font-semibold truncate whitespace-nowrap">{item.caption}</div>
            </div>
            <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-600/90 backdrop-blur text-[8px] sm:text-[9px] tracking-[0.15em] sm:tracking-[0.25em] text-white font-bold whitespace-nowrap shadow-[0_4px_14px_rgba(239,43,43,0.45)]">
              <span aria-hidden="true">▸</span>
              <span>TAP FOR BIO</span>
            </div>
          </div>
        </>
      ) : (
        // === FACILITY / CLASS CARDS (unchanged) ===
        <>
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
          <div className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-black/70 backdrop-blur border border-red-500/50">
            <span className="text-[8px] tracking-[0.4em] text-red-400 font-bold">{String(index + 1).padStart(2, '0')}</span>
          </div>
          {item.tag && (
            <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-red-600 text-[8px] tracking-[0.3em] font-bold">{item.tag}</div>
          )}
          <div className="absolute bottom-4 left-4 right-4">
            <div className="text-[8px] tracking-[0.4em] text-red-400 font-bold mb-1">{item.kind === 'class' ? 'PROGRAM' : 'FITNESSPRO · GALT'}</div>
            <div className="font-display text-xl md:text-2xl text-white leading-tight" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.95)' }}>{item.name}</div>
          </div>
        </>
      )}

      {/* Hover glint sweep */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="strip-glint absolute -inset-1/4" />
      </div>
    </div>
  );
}

/**
 * SwipeableTrack — the right-hand horizontal-scroll surface.
 *
 * Desktop: rendered exactly as before (overflow:hidden host, CSS marquee
 *   inside, edge fades, hover-pause).
 *
 * Touch (iPhone/iPad/Android): native horizontal scroll with scroll-snap
 *   (CSS-only, see .strip-track-mask @media (pointer: coarse), (hover: none)
 *   in index.css). On top of that we render two tiny ←/→ chevron buttons
 *   to *teach* first-time visitors that they can swipe. The chevrons:
 *     • fade in when the strip enters the viewport
 *     • stay until the user has scrolled the strip at least twice
 *       (any two scroll bursts > 30 px) — then fade out permanently for
 *       the rest of the session (sessionStorage flag)
 *     • tapping a chevron scrolls the track one card width (smoothly)
 *
 *   No animation library. Static inline SVG. ~3.5 KB of JS. Pointer-events
 *   disabled on desktop so the chevrons never interfere with the marquee.
 */
const SWIPE_DISMISSED_KEY = 'fp_strip_swipe_dismissed_v1';

function SwipeableTrack({ paused, setPaused, openBio }) {
  const maskRef = useRef(null);
  // hint shows when section is on-screen AND user hasn't swiped twice yet
  const [showHint, setShowHint] = useState(false);
  const swipeCountRef = useRef(0);
  const lastScrollLeftRef = useRef(0);

  // Reveal hint when the strip enters the viewport — but only if the
  // user hasn't already dismissed it earlier in the session, and only
  // on touch devices (chevrons are useless when there's a marquee).
  useEffect(() => {
    if (!IS_COARSE) return undefined;
    if (typeof window === 'undefined') return undefined;
    try {
      if (window.sessionStorage.getItem(SWIPE_DISMISSED_KEY)) return undefined;
    } catch (_) { /* sessionStorage blocked → still show */ }
    const el = maskRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setShowHint(true);
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Count swipes; after 2 distinct scroll bursts dismiss permanently.
  // A "burst" = scrollLeft moved by > 30 px since the last frame we
  // observed. We debounce via rAF so we don't count every scroll px.
  useEffect(() => {
    if (!IS_COARSE) return undefined;
    const el = maskRef.current;
    if (!el) return undefined;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const dx = Math.abs(el.scrollLeft - lastScrollLeftRef.current);
        if (dx > 30) {
          swipeCountRef.current += 1;
          lastScrollLeftRef.current = el.scrollLeft;
          if (swipeCountRef.current >= 2) {
            setShowHint(false);
            try { window.sessionStorage.setItem(SWIPE_DISMISSED_KEY, '1'); } catch (_) {}
            el.removeEventListener('scroll', onScroll);
          }
        }
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const scrollByCard = useCallback((direction) => {
    const el = maskRef.current;
    if (!el) return;
    // Find one card's width (+gap) so the snap lands cleanly on next card.
    const firstCard = el.querySelector('.strip-track > *');
    const cardW = firstCard ? firstCard.getBoundingClientRect().width + 20 : 260;
    el.scrollBy({ left: direction * cardW, behavior: 'smooth' });
  }, []);

  return (
    <div
      ref={maskRef}
      className="strip-track-mask relative flex-1 h-full overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Track wrapper — duplicated content for seamless loop.
          On coarse-pointer (mobile) devices we render a much smaller
          track (MOBILE_TRACK, ~10 items) and SKIP the duplicate
          copy entirely — mounting 66+ decoded image cards above
          the fold was the dominant cause of iPhone Safari "A
          problem repeatedly occurred" memory crashes. The CSS
          marquee animation is also paused on coarse pointers
          (see index.css). */}
      <div className={`strip-track h-full flex items-center gap-5 ${paused ? 'strip-track-paused' : ''}`}>
        {TRACK.map((item, i) => (
          <TrackCard key={`a-${i}`} item={item} index={i} isTrainer={item.kind === 'trainer'} onOpenBio={openBio} />
        ))}
        {TRACK.map((item, i) => (
          <TrackCard key={`b-${i}`} item={item} index={i} isTrainer={item.kind === 'trainer'} onOpenBio={openBio} />
        ))}
      </div>

      {/* Edge fades to suggest depth — narrower on mobile so more of
          the sliding cards remains clearly visible. */}
      <div className="absolute inset-y-0 left-0 w-8 sm:w-16 bg-gradient-to-r from-black to-transparent pointer-events-none z-10" />
      <div className="absolute inset-y-0 right-0 w-8 sm:w-16 bg-gradient-to-l from-black to-transparent pointer-events-none z-10" />

      {/* === SWIPE-AFFORDANCE CHEVRONS — touch-only, one-time per session === */}
      {/* Display gated entirely via CSS (.swipe-hint-chevron is display:none
          on desktop, display:flex inside @media (pointer: coarse), (hover:
          none)). The `swipe-hint-visible` class only controls the fade-in
          opacity once the section enters the viewport. */}
      <button
        type="button"
        aria-label="Scroll trainer cards left"
        data-testid="strip-swipe-prev"
        onClick={() => scrollByCard(-1)}
        className={`swipe-hint-chevron swipe-hint-left ${showHint ? 'swipe-hint-visible' : ''}`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <button
        type="button"
        aria-label="Scroll trainer cards right"
        data-testid="strip-swipe-next"
        onClick={() => scrollByCard(1)}
        className={`swipe-hint-chevron swipe-hint-right ${showHint ? 'swipe-hint-visible' : ''}`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    </div>
  );
}

export default function HeroDemoStrip() {
  const [paused, setPaused] = useState(false);
  const [hostIdx, setHostIdx] = useState(0);
  const [bioPerson, setBioPerson] = useState(null);
  const [bioOpen, setBioOpen] = useState(false);
  const closeTimerRef = React.useRef(null);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openBio = useCallback((person) => {
    if (!person) return;
    clearCloseTimer();
    setPaused(true);
    setBioPerson(person);
    setBioOpen(true);
  }, []);

  const handleSheetChange = useCallback((isOpen) => {
    if (isOpen) {
      setBioOpen(true);
      return;
    }
    // Keep `bioPerson` populated through the close-out animation so
    // Radix can fade the modal away with valid content. We clear it
    // shortly after the close completes (Radix animation ~200ms).
    setBioOpen(false);
    setPaused(false);
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setBioPerson(null);
      closeTimerRef.current = null;
    }, 280);
  }, []);

  // Cleanup on unmount
  useEffect(() => () => clearCloseTimer(), []);

  // Alternate Aracely <-> Amy every 3 seconds — DESKTOP ONLY. On touch
  // devices the LEFT_HOSTS card isn't rendered (MobileGalleryCard takes
  // its place) so there's no reason to keep ticking state. Also paused
  // when the bio sheet is open AND when the tab is hidden — saves CPU
  // for users who background the tab for long periods.
  useEffect(() => {
    if (bioOpen) return undefined;
    if (IS_COARSE) return undefined;
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      setHostIdx((i) => (i + 1) % LEFT_HOSTS.length);
    }, 3000);
    return () => clearInterval(id);
  }, [bioOpen]);

  return (
    <section
      data-testid="demo-strip"
      className="relative bg-black border-y border-zinc-900 py-10 overflow-hidden"
    >
      {/* Ambient glow */}
      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-[40%] h-[80%] bg-red-600/12 blur-[120px] pointer-events-none" />
      <div className="absolute inset-0 grid-bg opacity-15 pointer-events-none" />

      {/* Header strip */}
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 mb-6 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
          </span>
          <span className="text-[10px] tracking-[0.5em] text-red-500 font-bold">LIVE · ON THE FLOOR</span>
          <span className="hidden md:inline w-px h-4 bg-zinc-800" />
          <span className="hidden md:inline text-[10px] tracking-[0.4em] text-zinc-500 font-bold">ARACELY GUIDES YOU THROUGH FITNESSPRO</span>
        </div>
        {/* Status label — hover-based copy is desktop-only. Touch users
            (iPhone, iPad portrait + landscape, Android) see a swipe
            affordance — gated on `(pointer: coarse), (hover: none)` so
            iPad portrait (768 px) doesn't accidentally get the desktop
            wording via Tailwind's md:/lg: width breakpoints. */}
        <div className="text-[9px] tracking-[0.4em] text-zinc-600 font-bold">
          <span className="hide-on-touch">
            {paused ? 'PAUSED · MOVE AWAY TO RESUME' : 'AUTO PLAYING · HOVER TO PAUSE'}
          </span>
          <span className="show-on-touch">SWIPE TO EXPLORE →</span>
        </div>
      </div>

      {/* Stage: Aracely (left, sticky) + Track (right, scrolling).
          On TOUCH devices (iPhone, iPad portrait+landscape, Android) the
          right-side scrolling track is COMPLETELY removed and the 11-photo
          gallery takes the full available width. This is the production fix
          for the "two skinny side-by-side cards" mobile bug. Desktop layout
          is untouched. */}
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        {IS_COARSE ? (
          <div className="flex items-stretch h-[420px] md:h-[480px]" data-testid="mobile-gallery-stage">
            <MobileGalleryCard />
          </div>
        ) : (
        <div className="flex items-stretch gap-3 sm:gap-5 h-[420px] md:h-[480px]">
          {/* === LEFT HOST CARD — DESKTOP ONLY. Aracely↔Amy crossfade with
              TAP FOR BIO affordance. Hidden entirely on touch devices
              (see IS_COARSE branch above). */}
          <button
            type="button"
            onClick={() => openBio(LEFT_HOSTS[hostIdx].bio)}
            className="strip-aracely relative flex-shrink-0 w-[180px] sm:w-[260px] md:w-[320px] h-full rounded-3xl overflow-hidden border-[3px] border-red-600/65 shadow-[0_24px_60px_rgba(239,43,43,0.4)] bg-zinc-900 text-left transition-transform duration-300 hover:scale-[1.015] active:scale-[0.985] focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-black"
            data-testid="strip-host-card"
            data-cursor="hover"
            aria-label={`Read bio for ${LEFT_HOSTS[hostIdx].first} ${LEFT_HOSTS[hostIdx].last}`}
          >
            {/* Crossfading host portraits — only the active one is eager;
                the other is lazy so iOS Safari doesn't double-decode at
                first paint. */}
            {LEFT_HOSTS.map((host, i) => (
              <img
                key={host.first}
                src={host.src}
                alt={host.alt}
                loading={i === 0 ? 'eager' : 'lazy'}
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out"
                style={{
                  objectPosition: 'center 18%',
                  opacity: hostIdx === i ? 1 : 0,
                }}
              />
            ))}
            {/* Subtle glint sweep */}
            <div className="aracely-glint absolute inset-0 pointer-events-none" />
            {/* Edge gradients only — face stays clean */}
            <div className="absolute inset-x-0 bottom-0 h-[42%] bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="absolute inset-x-0 top-0 h-[14%] bg-gradient-to-b from-black/60 to-transparent" />

            {/* Top row — badge + HOST pip share one flex container so they
                never overlap, regardless of card width. */}
            <div className="absolute top-2 left-2 right-2 sm:top-3 sm:left-3 sm:right-3 z-10 flex items-center justify-between gap-1.5 sm:gap-2">
              <div className="flex-shrink min-w-0 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-red-600 shadow-[0_4px_18px_rgba(239,43,43,0.5)] transition-all duration-500">
                <span
                  key={LEFT_HOSTS[hostIdx].badge}
                  className="block text-[8px] sm:text-[9px] tracking-[0.08em] sm:tracking-[0.3em] font-bold animate-host-fade whitespace-nowrap"
                >
                  {LEFT_HOSTS[hostIdx].badge}
                </span>
              </div>
              <div className="flex-shrink-0 flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-black/75 backdrop-blur border border-red-500/55">
                <span className="relative flex h-1 w-1">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-1 w-1 bg-red-500" />
                </span>
                <span className="text-[8px] sm:text-[9px] tracking-[0.08em] sm:tracking-[0.3em] text-white font-bold">HOST</span>
              </div>
            </div>

            {/* Bottom name plate — crossfades with host */}
            <div className="absolute bottom-0 left-0 right-0 px-3 sm:px-4 pb-3 sm:pb-4 z-10">
              <div
                key={`plate-${hostIdx}`}
                className="animate-host-fade"
              >
                <div className="flex items-center gap-1.5 sm:gap-2 text-[8px] sm:text-[9px] tracking-[0.18em] sm:tracking-[0.4em] text-red-400 font-bold mb-1.5 whitespace-nowrap">
                  <div className="h-[1.5px] w-4 sm:w-5 bg-red-500 flex-shrink-0" />
                  <span className="truncate">{LEFT_HOSTS[hostIdx].eyebrow}</span>
                </div>
                <div className="font-display text-xl sm:text-2xl md:text-3xl text-white leading-none truncate" style={{ textShadow: '0 4px 20px rgba(0,0,0,0.95)' }}>
                  {LEFT_HOSTS[hostIdx].first} <span className="text-red-500">{LEFT_HOSTS[hostIdx].last}</span>
                </div>
                <div className="mt-1.5 sm:mt-2 text-[8px] sm:text-[9px] tracking-[0.12em] sm:tracking-[0.3em] text-zinc-300 font-semibold whitespace-nowrap truncate">{LEFT_HOSTS[hostIdx].caption}</div>
              </div>
              {/* Tap-to-read affordance — keeps it discoverable on touch */}
              <div className="mt-2 sm:mt-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-600/90 backdrop-blur text-[8px] sm:text-[9px] tracking-[0.15em] sm:tracking-[0.25em] text-white font-bold whitespace-nowrap shadow-[0_4px_14px_rgba(239,43,43,0.45)]">
                <span aria-hidden="true">▸</span>
                <span>TAP FOR BIO</span>
              </div>
            </div>
          </button>

          {/* === GLIDING TRACK — desktop only (left-to-right infinite scroll).
              Removed from touch devices entirely; mobile/iPad shows the
              11-photo MobileGalleryCard above instead. === */}
          <SwipeableTrack paused={paused} setPaused={setPaused} openBio={openBio} />
        </div>
        )}
      </div>

      {/* Bio sheet — opens on tap of any host or trainer card */}
      <BioSheet open={bioOpen} onOpenChange={handleSheetChange} person={bioPerson} />
    </section>
  );
}
